import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
//controls
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  Modal,
  ModalBody,
  InputGroup,
  FormFeedback,
  ModalHeader,
  ModalFooter,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import Switch from "react-switch"

//react form
import { FormProvider, useForm, Controller } from "react-hook-form"
import { createSelector } from "reselect"
import Select, { components } from "react-select"
import "flatpickr/dist/flatpickr.css"

//validation
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"

//redux
import { useSelector, useDispatch } from "react-redux"

//custom hooks
import { useDeepCompareEffect } from "hooks"

//Date filter
import Moment from "moment"

import { addNewDocument as onAddDocument } from "store/actions"
import { OnSymbol, Offsymbol } from "helpers/switch_helper"
import { EnumObjectTypes } from "helpers/enum_helper"

const DocumentModal = ({
  show,
  objectID,
  objectTypeID,
  data,
  onCloseClick,
}) => {
  const dispatch = useDispatch()

  const { uploading } = useSelector(state => state.document)
  const [isUploading, setUploading] = useState(uploading)

  useEffect(() => {
    if (data == null) {
      reset(formDefault)
    } else {
      reset(data)
    }
  }, [dispatch])

  //////////////////DOCUMENT STATE/////////////////////
  const { privacytypes } = useSelector(state => state.document)
  ///////////////////////////////////////
  const MAX_FILE_SIZE = process.env.REACT_APP_MAX_FILE_SIZE
    ? Number(process.env.REACT_APP_MAX_FILE_SIZE)
    : 20 * 1024 * 1024 // Default to 20MB
  ///////////////////////////////////////

  /**
   * Form Validation Schema
   */
  const schema = yup.object().shape({
    ObjectArtifactID: yup.number(),
    // Attachment: yup.mixed().when("ObjectArtifactID", {
    //   is: val => val === 0,
    //   then: schema => schema.required("Required"),
    //   otherwise: schema => schema.nullable(),
    // }),
    //.transform((_, val) => (val ? String(val) : null)),
    Attachment: yup
      .mixed()
      .test("fileSize", "File size should be less than 20MB", value => {
        if (!value) return true // Allow empty file
        return value.size <= MAX_FILE_SIZE //20 * 1024 * 1024 // 20MB limit
      })
      .when("ObjectArtifactID", {
        is: val => val === 0,
        then: schema => schema.required("Required"),
        otherwise: schema => schema.nullable(),
      }),
    ObjectID: yup.number(),
    ObjectTypeID: yup.number(),
    ExpiryDate: yup.string(),
    Description: yup.string(),
    IsActive: yup.bool(),
  })

  const formDefault = {
    ObjectArtifactID: 0,
    Attachment: null,
    ExpiryDate: "",
    ObjectID: objectID,
    ObjectTypeID: objectTypeID,
    IsActive: true,
    Description: "",
  }

  const methods = useForm({
    mode: "onChange",
    defaultValues: formDefault,
    resolver: yupResolver(schema),
  })
  const { reset, control, formState, watch, setValue, getValues, trigger } =
    methods
  const { errors, isValid } = formState

  const onSaveClick = () => {
    trigger()
    if (isValid) {
      setUploading(true)
      dispatch(onAddDocument(getValues()))
      onCloseClick()
    }
  }

  const onClosed = () => {
    reset()
  }

  return (
    <Modal
      isOpen={show}
      toggle={onCloseClick}
      onClosed={() => onClosed()}
      centered={true}
    >
      <ModalHeader className="d-flex justify-content-center">
        Uploading Document
      </ModalHeader>
      <ModalBody className="py-3 px-5">
        <FormProvider {...methods}>
          <Row>
            {isUploading ? (
              <div className="placeholder-glow">
                <Label>Document</Label>
                <span className="placeholder col-12"></span>
                <Label>Expiration Date</Label>
                <span className="placeholder col-12"></span>
                <Label>Privacy</Label>
                <span className="placeholder col-12"></span>
                <Label>File Description</Label>
                <span className="placeholder col-12"></span>
              </div>
            ) : (
              <Col lg={12}>
                <div className="mb-3">
                  <Label>Document</Label>
                  <Controller
                    name="Attachment"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Input
                          {...field}
                          className="form-control"
                          type="file"
                          id="Attachment"
                          value={field.value?.Attachment}
                          onChange={e => field.onChange(e.target.files[0])}
                          invalid={!!errors.Attachment}
                        />
                        {errors?.Attachment?.message ? (
                          <FormFeedback type="invalid" className="d-block">
                            {errors?.Attachment?.message}
                          </FormFeedback>
                        ) : null}
                      </>
                    )}
                  />
                  <a>{data && "Document : " + getValues("FileName")}</a>
                </div>
                <div className="mb-3">
                  <Label>Expiration Date</Label>
                  <Controller
                    name="ExpiryDate"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Flatpickr
                          {...field}
                          className="form-control d-block"
                          id="ExpiryDate"
                          options={{
                            dateFormat: "d M, Y",
                          }}
                          onChange={(selectedDates, dateStr, instance) => {
                            setValue("ExpiryDate", dateStr)
                          }}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="mb-3">
                  <Label>File Description</Label>
                  <Controller
                    name="Description"
                    control={control}
                    value=""
                    render={({ field }) => (
                      <textarea
                        {...field}
                        rows={3}
                        className="form-control mb-3"
                        id="Description"
                      />
                    )}
                  />
                </div>
                <div className="mb-3">
                  <Label>Active</Label>
                  <div>
                    <Controller
                      name="IsActive"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Switch
                            {...field}
                            id="IsActive"
                            checked={field.value}
                            uncheckedIcon={<Offsymbol />}
                            checkedIcon={<OnSymbol />}
                            className="me-1 mb-sm-8 mb-2"
                            onColor="#626ed4"
                          />
                        </>
                      )}
                    />
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </FormProvider>
      </ModalBody>
      <ModalFooter>
        {isUploading ? (
          <>
            <a className="btn btn-primary disabled placeholder col-3"></a>
            <a className="btn btn-secondary disabled placeholder col-3"></a>
          </>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn btn-primary"
              onClick={() => onSaveClick()}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn btn-secondary"
              onClick={onCloseClick}
            >
              Cancel
            </button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  )
}

DocumentModal.propTypes = {
  onCloseClick: PropTypes.func,
  onSaveClick: PropTypes.func,
  show: PropTypes.any,
}

export default DocumentModal
